import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, ArrowRight, Building2, Stethoscope, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription, daysUntil } from "@/context/SubscriptionContext";

const ROLE_ICON = { patient: HeartPulse, doctor: Stethoscope, hospital: Building2 };

export default function Upgrade() {
  const { user } = useAuth();
  const { subscription, is_pro, eligible_plan, effective_status, startTrial, createCheckout, refresh } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/subscription/plans");
      setPlans(data.plans || []);
    })();
    if (searchParams.get("canceled") === "1") {
      toast.info("Checkout canceled — you weren't charged.");
    }
  }, [searchParams]);

  const onStartTrial = async () => {
    if (!eligible_plan) return;
    setBusy(true);
    try {
      await startTrial(eligible_plan.id);
      toast.success(`${eligible_plan.name} trial started — ${eligible_plan.trial_days} days of full access.`);
      navigate("/billing", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onCheckout = async () => {
    if (!eligible_plan) return;
    setBusy(true);
    try {
      const { url } = await createCheckout(eligible_plan.id);
      window.location.href = url;  // hand-off to Stripe
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const canStartTrial = !subscription?.trial_started_at && effective_status === "free";
  const trialDaysLeft = subscription?.trial_ends_at ? daysUntil(subscription.trial_ends_at) : null;

  return (
    <AppShell
      pageTitle="Upgrade to Pro"
      pageDescription="Unlock analytics, verified badges, and priority DPDP support."
    >
      {/* Eligible plan hero */}
      {eligible_plan && (
        <div className="rounded-xl border border-border bg-primary text-primary-foreground p-6 md:p-10" data-testid="upgrade-hero">
          <div className="flex flex-wrap items-start gap-6">
            <div className="w-14 h-14 rounded-md bg-primary-foreground/10 flex items-center justify-center shrink-0">
              {React.createElement(ROLE_ICON[eligible_plan.role] || Sparkles, { size: 22 })}
            </div>
            <div className="min-w-0 flex-1">
              <div className="overline text-primary-foreground/70">Recommended for your account</div>
              <h2 className="mt-2 font-serif text-4xl leading-tight">{eligible_plan.name}</h2>
              <div className="mt-3 text-3xl font-serif">
                ₹{eligible_plan.price_inr.toLocaleString("en-IN")}
                <span className="text-base text-primary-foreground/70"> / month</span>
              </div>
              <p className="mt-1 text-sm text-primary-foreground/80">
                {eligible_plan.trial_days}-day free trial. Cancel anytime.
              </p>
              <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                {eligible_plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={14} className="mt-1 shrink-0" /> <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {canStartTrial && (
                  <Button
                    onClick={onStartTrial}
                    disabled={busy}
                    className="bg-white text-primary hover:bg-white/90 h-11 px-5"
                    data-testid="upgrade-start-trial"
                  >
                    Start {eligible_plan.trial_days}-day free trial <ArrowRight size={16} className="ml-1" />
                  </Button>
                )}
                <Button
                  onClick={onCheckout}
                  disabled={busy}
                  variant={canStartTrial ? "outline" : "default"}
                  className={canStartTrial ? "border-white/40 text-primary-foreground hover:bg-white/10 h-11 px-5" : "bg-white text-primary hover:bg-white/90 h-11 px-5"}
                  data-testid="upgrade-checkout"
                >
                  {busy ? "Redirecting…" : "Subscribe with card"}
                </Button>
                {is_pro && (
                  <Link to="/billing" className="text-sm underline text-primary-foreground/85" data-testid="upgrade-manage-link">
                    Manage current plan
                  </Link>
                )}
              </div>

              {trialDaysLeft !== null && effective_status === "trialing" && (
                <div className="mt-3 text-xs text-primary-foreground/75" data-testid="upgrade-trial-status">
                  You are on a free trial — {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* All plans, so users can see the full pricing table */}
      <div className="mt-8">
        <div className="overline">All plans</div>
        <h3 className="mt-1 font-serif text-2xl text-primary">Every role, one clear price.</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-grid">
          {plans.map((p, idx) => {
            const isMine = p.role === user?.role;
            const Icon = ROLE_ICON[p.role] || Sparkles;
            const tones = ["bg-pastel-mint-bg border-pastel-mint-border", "bg-pastel-sky-bg border-pastel-sky-border", "bg-pastel-lavender-bg border-pastel-lavender-border"];
            const cardTone = isMine ? "bg-white border-primary" : tones[idx % tones.length];
            return (
              <div
                key={p.id}
                className={`rounded-lg border ${cardTone} p-5 transition-transform duration-200 hover:-translate-y-0.5`}
                data-testid={`pricing-card-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  <Icon size={18} className="text-primary" />
                  {isMine && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest rounded-full bg-primary/10 text-primary px-2 py-0.5">
                      Your role
                    </span>
                  )}
                </div>
                <div className="mt-3 font-serif text-2xl text-primary">{p.name}</div>
                <div className="mt-1 text-sm text-muted-foreground capitalize">{p.role}</div>
                <div className="mt-4 font-serif text-3xl text-primary">
                  ₹{p.price_inr.toLocaleString("en-IN")}
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>
                <div className="text-xs text-muted-foreground">{p.trial_days}-day free trial</div>
                <ul className="mt-4 space-y-1.5 text-xs text-foreground">
                  {p.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check size={11} className="text-primary mt-0.5 shrink-0" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-8 text-xs text-muted-foreground max-w-2xl">
        Payments processed by Stripe in INR. GST included. Cancel any time from the Billing page — you keep Pro access until the end of your current period.
      </p>
    </AppShell>
  );
}
